import { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentLocationDetails, haversineDistance, getStoredCoords, storeCoords } from './location';
import { authenticatedFetch } from './apiHelpers';
import {
  GET_ACTIVE_LOCATION_URL,
  UPDATE_CURRENT_LOCATION_URL,
} from '../../config/api';

export interface MovedLocation {
  lat: number;
  lon: number;
  city: string;
  address: string;
}

interface UseLocationFlowReturn {
  movedLocation: MovedLocation | null;
  confirmLocationUpdate: () => Promise<void>;
  dismissLocationUpdate: () => void;
}

const MOVEMENT_THRESHOLD_KM = 0.5;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function syncActiveLocation(token: string): Promise<void> {
  try {
    const res = await authenticatedFetch(GET_ACTIVE_LOCATION_URL);
    if (!res.ok) return;
    const json = await res.json();
    const city: string | undefined =
      json?.current_location?.city ??
      json?.active_address?.city ??
      json?.city;
    const lat: number | undefined =
      json?.current_location?.latitude ??
      json?.active_address?.latitude;
    const lon: number | undefined =
      json?.current_location?.longitude ??
      json?.active_address?.longitude;

    if (city) {
      localStorage.setItem('activeCity', city.trim());
      window.dispatchEvent(new Event('activeCityChanged'));
    }
    if (lat != null && lon != null && !isNaN(lat) && !isNaN(lon)) {
      storeCoords(lat, lon);
    }
  } catch {
    // non-critical
  }
}

export function useLocationFlow(isLoggedIn: boolean): UseLocationFlowReturn {
  const [movedLocation, setMovedLocation] = useState<MovedLocation | null>(null);
  const pendingCoords = useRef<{ lat: number; lon: number; city: string; address: string } | null>(null);

  // On app open: fetch active location from API; if none, request GPS
  useEffect(() => {
    if (!isLoggedIn) return;

    const init = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const res = await authenticatedFetch(GET_ACTIVE_LOCATION_URL);
        if (res.ok) {
          const json = await res.json();
          const city: string | undefined =
            json?.current_location?.city ??
            json?.active_address?.city ??
            json?.city;
          const lat: number | undefined =
            json?.current_location?.latitude ??
            json?.active_address?.latitude;
          const lon: number | undefined =
            json?.current_location?.longitude ??
            json?.active_address?.longitude;

          if (city) {
            localStorage.setItem('activeCity', city.trim());
            window.dispatchEvent(new Event('activeCityChanged'));
          }

          if (lat != null && lon != null && !isNaN(lat) && !isNaN(lon)) {
            storeCoords(lat, lon);
            return; // has a saved location, done
          }
        }
      } catch {
        // fall through to GPS
      }

      // No saved location — ask GPS silently
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const result = await getCurrentLocationDetails();
            if ('type' in result) return;
            storeCoords(latitude, longitude);
            localStorage.setItem('activeCity', result.city.trim());
            window.dispatchEvent(new Event('activeCityChanged'));
            await authenticatedFetch(
              UPDATE_CURRENT_LOCATION_URL(latitude, longitude, result.full_address),
              { method: 'POST', headers: { accept: 'application/json' } }
            );
          } catch {
            // non-critical
          }
        },
        () => {/* permission denied — silently ignore */},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    };

    init();
  }, [isLoggedIn]);

  // 5-minute polling: silent GPS check; fire toast only if moved > 0.5 km
  useEffect(() => {
    if (!isLoggedIn) return;

    const check = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          const stored = getStoredCoords();
          if (!stored) {
            storeCoords(latitude, longitude);
            return;
          }
          const dist = haversineDistance(stored.lat, stored.lon, latitude, longitude);
          if (dist < MOVEMENT_THRESHOLD_KM) return;

          // Significant movement detected — reverse geocode silently
          try {
            const result = await getCurrentLocationDetails();
            if ('type' in result) return;
            pendingCoords.current = {
              lat: latitude,
              lon: longitude,
              city: result.city,
              address: result.full_address,
            };
            setMovedLocation({
              lat: latitude,
              lon: longitude,
              city: result.city,
              address: result.full_address,
            });
          } catch {
            // non-critical
          }
        },
        () => {/* silently ignore */},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    };

    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoggedIn]);

  const confirmLocationUpdate = useCallback(async () => {
    const coords = pendingCoords.current;
    if (!coords) { setMovedLocation(null); return; }

    try {
      await authenticatedFetch(
        UPDATE_CURRENT_LOCATION_URL(coords.lat, coords.lon, coords.address),
        { method: 'POST', headers: { accept: 'application/json' } }
      );
      storeCoords(coords.lat, coords.lon);
      localStorage.setItem('activeCity', coords.city.trim());
      window.dispatchEvent(new Event('activeCityChanged'));
    } catch {
      // non-critical
    }
    pendingCoords.current = null;
    setMovedLocation(null);
  }, []);

  const dismissLocationUpdate = useCallback(() => {
    pendingCoords.current = null;
    setMovedLocation(null);
  }, []);

  return { movedLocation, confirmLocationUpdate, dismissLocationUpdate };
}

export { syncActiveLocation };
